<template>
  <DropdownMenu v-model:open="open">
    <DropdownMenuTrigger as-child>
      <Button variant="ghost" size="icon" class="relative">
        <Bell class="size-5" />
        <span
          v-if="unreadCount > 0"
          class="absolute -top-0.5 -right-0.5 flex size-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-white"
        >
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </span>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="end"
      :side-offset="8"
      class="w-[calc(100vw-2rem)] max-w-[360px] p-0"
    >
      <div class="flex items-center justify-between px-3 py-2.5">
        <span class="text-sm font-semibold">Notificaciones</span>
        <Button
          v-if="unreadCount > 0"
          variant="ghost"
          size="xs"
          class="h-7 text-xs text-primary"
          @click="markAllAsRead"
        >
          <Check class="size-3.5" />
          Marcar todas como leídas
        </Button>
      </div>
      <Separator />
      <ScrollArea class="max-h-[320px]">
        <div v-if="notifications.length > 0" class="flex flex-col">
          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="relative flex items-start gap-3 px-3 py-3 cursor-pointer border-b last:border-b-0 transition-colors hover:bg-accent"
            :class="{ 'bg-primary/5': !notification.is_read }"
            @click="handleNotificationClick(notification)"
          >
            <div
              class="flex size-9 shrink-0 items-center justify-center rounded-full"
              :class="iconClass(notification.type)"
            >
              <component :is="iconFor(notification.type)" class="size-4" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="text-[13px] font-medium leading-snug">
                {{ notification.title }}
              </div>
              <div class="mt-0.5 line-clamp-2 text-xs text-muted-foreground leading-snug">
                {{ notification.message }}
              </div>
              <div class="mt-1 text-[11px] text-muted-foreground">
                {{ formatTime(notification.created_at) }}
              </div>
            </div>
            <button
              v-if="!notification.is_read"
              class="size-2 shrink-0 self-center rounded-full bg-primary transition-colors hover:bg-primary/70"
              aria-label="Marcar como leída"
              @click.stop="markAsRead(notification.id)"
            />
          </div>
        </div>
        <div
          v-else
          class="flex flex-col items-center justify-center py-12 text-muted-foreground"
        >
          <BellOff class="size-10 opacity-50" />
          <p class="mt-3 text-sm">Sin notificaciones</p>
        </div>
      </ScrollArea>
      <Separator />
      <div class="p-1">
        <Button
          variant="ghost"
          size="sm"
          class="w-full justify-center text-xs"
          @click="handleCommand('view-all')"
        >
          Ver todas
        </Button>
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, type Component } from 'vue'
import { useRouter } from 'vue-router'
import { Bell, BellOff, Check, User, Timer, RefreshCw } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { useNotificationStore } from '@/stores/notifications'
import { useAuthStore } from '@/stores/auth'
import type { NotificationResponse } from '@/api/notification'

const router = useRouter()
const notificationStore = useNotificationStore()
const authStore = useAuthStore()

const open = ref(false)

const notifications = computed(() => notificationStore.notifications)
const unreadCount = computed(() => notificationStore.unreadCount)

const loadNotifications = () => {
  notificationStore.loadNotifications(false, 20)
}

const markAsRead = async (notificationId: number) => {
  await notificationStore.markAsRead(notificationId)
}

const markAllAsRead = async () => {
  await notificationStore.markAllAsRead()
}

const handleNotificationClick = async (notification: NotificationResponse) => {
  if (!notification.is_read) {
    await markAsRead(notification.id)
  }

  if (notification.data?.lead_id) {
    open.value = false
    router.push(`/leads/${notification.data.lead_id}`)
  }
}

const handleCommand = (command: string) => {
  if (command === 'view-all') {
  }
}

const iconFor = (type: string): Component => {
  switch (type) {
    case 'lead_assigned':
      return User
    case 'callback_reminder':
      return Timer
    case 'lead_status_changed':
      return RefreshCw
    default:
      return Bell
  }
}

const iconClass = (type: string): string => {
  switch (type) {
    case 'lead_assigned':
      return 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400'
    case 'callback_reminder':
      return 'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400'
    case 'lead_status_changed':
      return 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

const formatTime = (dateString: string) => {
  const date = new Date(dateString)
  const now = new Date()
  const diff = now.getTime() - date.getTime()

  if (diff < 60000) {
    return 'Hace un momento'
  }
  if (diff < 3600000) {
    const minutes = Math.floor(diff / 60000)
    return `Hace ${minutes} minuto${minutes > 1 ? 's' : ''}`
  }
  if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000)
    return `Hace ${hours} hora${hours > 1 ? 's' : ''}`
  }
  if (diff < 604800000) {
    const days = Math.floor(diff / 86400000)
    return `Hace ${days} día${days > 1 ? 's' : ''}`
  }

  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

onMounted(() => {
  loadNotifications()
  if (authStore.token) {
    notificationStore.initWebSocket()
  }
})

watch(
  () => authStore.token,
  (newToken) => {
    if (newToken) {
      notificationStore.initWebSocket()
    } else {
      notificationStore.disconnectWebSocket()
    }
  }
)
</script>